import path from 'path';
import fs from 'fs-extra';

export interface Doc {
  id: string;
  title: string;
  type: 'markdown' | 'prd' | 'api' | 'design' | 'other';
  path: string;
  size: number;
  created: string;
  updated: string;
}

export class DocService {
  private docsDir: string;

  constructor(workflowDir: string = path.join(process.cwd(), '.workflow')) {
    this.docsDir = path.join(workflowDir, 'docs');
    this.ensureDocsDir();
  }

  private ensureDocsDir(): void {
    fs.ensureDirSync(this.docsDir);
  }

  async list(): Promise<Doc[]> {
    const docs: Doc[] = [];
    
    try {
      const files = await fs.readdir(this.docsDir);
      
      for (const file of files) {
        const filePath = path.join(this.docsDir, file);
        const stat = await fs.stat(filePath);
        
        if (stat.isFile() && (file.endsWith('.md') || file.endsWith('.txt'))) {
          const doc: Doc = {
            id: file.replace(/\.(md|txt)$/, ''),
            title: file.replace(/[-_]/g, ' ').replace(/\.(md|txt)$/, ''),
            type: this.getDocType(file),
            path: filePath,
            size: stat.size,
            created: stat.birthtime.toISOString(),
            updated: stat.mtime.toISOString()
          };
          docs.push(doc);
        }
      }
    } catch (error) {
      console.error('Failed to list docs:', error);
    }
    
    return docs;
  }

  private getDocType(filename: string): Doc['type'] {
    const lower = filename.toLowerCase();
    if (lower.includes('prd')) return 'prd';
    if (lower.includes('api')) return 'api';
    if (lower.includes('design')) return 'design';
    if (lower.endsWith('.md')) return 'markdown';
    return 'other';
  }

  async get(id: string): Promise<Doc | null> {
    const docs = await this.list();
    return docs.find(d => d.id === id) || null;
  }

  async getContent(id: string): Promise<string | null> {
    const doc = await this.get(id);
    if (!doc) return null;
    
    try {
      return await fs.readFile(doc.path, 'utf-8');
    } catch (error) {
      console.error(`Failed to read doc ${id}:`, error);
      return null;
    }
  }
}